// Constants
import { HttpHeaders } from "@ms/uno-constants/lib/local/HttpConstants";
import { HttpStatus } from "@ms/uno-constants/lib/local/HttpStatusConstants";
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
/**
 * Ajax client decorator that limits concurrent requests and circuit-breaks on repeated 429 responses.
 *
 * Concurrency: Maintains a count of in-flight requests. When the limit is reached, new requests
 * queue in FIFO order until a slot frees up.
 *
 * Circuit breaker: After 429 (Too Many Requests) responses accumulate without an intervening success,
 * and the count reaches a configurable threshold, all outgoing requests are paused for the Retry-After
 * duration (or a default pause). Non-429 failures do not reset the counter — only a successful response
 * does. This prevents retry storms where individual request retries amplify throttling.
 */ export class ThrottleAjaxClient {
    async executeRequest(url, options) {
        // Wait for a concurrency slot
        await this.acquireSlot();
        // Check circuit breaker AFTER acquiring slot so queued requests also wait
        if (this.circuitBreakerPromise) {
            await this.circuitBreakerPromise;
        }
        try {
            const response = await this.innerAjaxClient.executeRequest(url, options);
            this.onSuccess();
            return response;
        } catch (error) {
            this.onFailure(error);
            throw error;
        } finally{
            this.releaseSlot();
        }
    }
    /**
     * Waits until a concurrency slot is available.
     * If under the limit, resolves immediately. Otherwise queues the request.
     */ acquireSlot() {
        if (this.inFlightCount < this.config.maxConcurrentRequests) {
            this.inFlightCount++;
            return Promise.resolve();
        }
        return new Promise((resolve)=>{
            this.waitQueue.push({
                resolve
            });
            this.loggers.traceLogger.logTrace(0x1e04b5df /* tag_4blx5 */ , TraceLevel.Warning, `Request queued — concurrency limit reached [InFlight=${this.inFlightCount}][QueueDepth=${this.waitQueue.length}]`);
        });
    }
    /**
     * Releases a concurrency slot and processes the next queued request if any.
     */ releaseSlot() {
        const next = this.waitQueue.shift();
        if (next) {
            // Hand the slot directly to the next waiter (inFlightCount stays the same)
            next.resolve();
        } else {
            this.inFlightCount--;
        }
    }
    /**
     * Called on successful response. Resets the 429 counter.
     */ onSuccess() {
        if (this.count429SinceLastSuccess > 0) {
            this.count429SinceLastSuccess = 0;
        }
    }
    /**
     * Called on failed response. Tracks 429s since last success and triggers circuit breaker when threshold is reached.
     * Non-429 failures are ignored — only a successful response resets the counter.
     */ onFailure(failureResult) {
        const status = failureResult.response?.status;
        if (status === HttpStatus.TooManyRequests) {
            this.count429SinceLastSuccess++;
            if (this.count429SinceLastSuccess >= this.config.circuitBreakerThreshold && !this.circuitBreakerOpen) {
                this.openCircuitBreaker(failureResult.response);
            }
        } else {
        // Non-429 failure does not reset the counter — only success resets it
        }
    }
    /**
     * Opens the circuit breaker, pausing all new requests for the Retry-After duration.
     */ openCircuitBreaker(response) {
        this.circuitBreakerOpen = true;
        const retryAfterRaw = response?.headers.get(HttpHeaders.RetryAfter) ?? null;
        const pauseMs = this.getRetryAfterMs(response);
        this.loggers.traceLogger.logTrace(0x1e04b68b /* tag_4bl0l */ , TraceLevel.Warning, `Circuit breaker opened — pausing all requests [Count429SinceLastSuccess=${this.count429SinceLastSuccess}][RetryAfterHeader=${retryAfterRaw}][PauseMs=${pauseMs}]`);
        this.circuitBreakerPromise = new Promise((resolve)=>{
            setTimeout(()=>{
                this.circuitBreakerOpen = false;
                this.circuitBreakerPromise = null;
                this.count429SinceLastSuccess = 0;
                this.loggers.traceLogger.logTrace(0x1e04b68a /* tag_4bl0k */ , TraceLevel.Info, `Circuit breaker closed — resuming requests`);
                resolve();
            }, pauseMs);
        });
    }
    /**
     * Extracts the pause duration from the Retry-After header on a 429 response.
     * Falls back to the configured default if the header is absent or not parseable.
     * Clamped to maxCircuitBreakerPauseMs to prevent unreasonably long pauses.
     */ getRetryAfterMs(response) {
        const retryAfterValue = response?.headers.get(HttpHeaders.RetryAfter) ?? null;
        if (!retryAfterValue) {
            return this.config.circuitBreakerPauseMs;
        }
        const retryAfterSeconds = Number(retryAfterValue);
        if (!isNaN(retryAfterSeconds) && retryAfterSeconds > 0) {
            return Math.min(retryAfterSeconds * 1000, this.config.maxCircuitBreakerPauseMs);
        }
        return this.config.circuitBreakerPauseMs;
    }
    constructor(ajaxClient, config, loggers){
        /** Number of requests currently in-flight */ this.inFlightCount = 0;
        /** FIFO queue of requests waiting for a concurrency slot */ this.waitQueue = [];
        /** Count of 429 responses since the last success (non-429 failures do not reset this) */ this.count429SinceLastSuccess = 0;
        /** Whether the circuit breaker is currently open (pausing all requests) */ this.circuitBreakerOpen = false;
        /** Promise that resolves when the circuit breaker pause ends */ this.circuitBreakerPromise = null;
        this.innerAjaxClient = ajaxClient;
        this.config = config;
        this.loggers = loggers;
        loggers.traceLogger.logTrace(0x1e04b68c /* tag_4bl0m */ , TraceLevel.Verbose, `Initializing ThrottleAjaxClient [MaxConcurrent=${config.maxConcurrentRequests}][CircuitBreakerThreshold=${config.circuitBreakerThreshold}][CircuitBreakerPauseMs=${config.circuitBreakerPauseMs}]`);
    }
}
